import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

export interface FormSectionProps {
  title: string;
  description?: string;
  collapsedDescription?: string; // Description to show when collapsed (instead of description)
  children: ReactNode;
  defaultOpen?: boolean;
  collapsible?: boolean;
}

export function FormSection({
  title,
  description,
  collapsedDescription,
  children,
  defaultOpen = true,
  collapsible = false,
}: FormSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  if (!collapsible) {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-medium text-foreground">{title}</h3>
          {description && (
            <p className="mt-0.5 text-sm text-foreground-muted">{description}</p>
          )}
        </div>
        <div className="space-y-4">{children}</div>
      </div>
    );
  }

  // Determine which description to show based on open state
  const displayDescription = isOpen ? description : (collapsedDescription || description);

  return (
    <div className="border border-border rounded-lg">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-background-elevated transition-colors rounded-lg"
      >
        <div className="flex-1">
          <h3 className="text-sm font-medium text-foreground">{title}</h3>
          {displayDescription && (
            <p className="mt-0.5 text-sm text-foreground-muted">{displayDescription}</p>
          )}
        </div>
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-foreground-muted flex-shrink-0 ml-2" />
        ) : (
          <ChevronRight className="h-4 w-4 text-foreground-muted flex-shrink-0 ml-2" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-border space-y-4">
          {children}
        </div>
      )}
    </div>
  );
}
